import React from 'react';
import { StandardTableActions, Badge, statusVariantFromBoolean } from '../../components/ui';

import { Column } from '../../components/DataTable/DataTable';
import { RecurringPayable } from './useData';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { APP_CONFIG } from '../../config/appConfig';
import { ThemeColors } from '../../contexts/ThemeContext';

export const moduleConfig = {
  module: {
    title: 'Recurring Payables',
    description: 'Recurring payables for projection',
    permissionModuleKey: 'recurring_payables',
    route: {
      list: '/recurring-payables',
      new: '/recurring-payables/new',
      detail: (id: number) => `/recurring-payables/${id}`,
      edit: (id: number) => `/recurring-payables/${id}/edit`,
    },
    api: { list: '/recurring-payables', detail: (id: number) => `/recurring-payables/${id}` },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: { placeholder: 'Search by payee...' },
  filterGroups: [
    { name: 'basic', label: 'Basic', icon: '🔍', fields: ['isActive', 'payeeName', 'categoryId', 'recurrenceIntervalId', 'startDate', 'amount'] },
  ],
  filters: {
    fields: [
      { key: 'payeeName', label: 'Payee', placeholder: 'Search payee...', type: 'text' as const, getValue: (item: RecurringPayable) => item.payeeName, options: [] },
      { key: 'categoryId', label: 'Category', placeholder: 'All categories', type: 'select' as const, getValue: (item: RecurringPayable) => item.categoryDescr?.name ?? (item.categoryId != null ? `#${item.categoryId}` : ''), options: [], apiEndpoint: '/crm-expense-category', labelField: 'name' },
      { key: 'recurrenceIntervalId', label: 'Recurrence', placeholder: 'All', type: 'select' as const, getValue: (item: RecurringPayable) => item.recurrenceInterval?.tri_descr ?? (item.recurrenceIntervalId != null ? `#${item.recurrenceIntervalId}` : ''), options: [], apiEndpoint: '/p-crm-todo-recurrence-interval', labelField: 'tri_descr' },
      { key: 'startDate', label: 'Start date', placeholder: 'All dates', type: 'date' as const, getValue: (item: RecurringPayable) => (item.startDate ? moduleConfig.utils.formatDate(item.startDate) : undefined), options: [] },
      { key: 'amount', label: 'Amount', placeholder: 'Amount', type: 'number' as const, getValue: (item: RecurringPayable) => item.amount?.toString(), options: [] },
      { key: 'isActive', label: 'Status', placeholder: 'All', type: 'boolean' as const, getValue: (item: RecurringPayable) => (item.isActive ? 'Active' : 'Inactive'), options: ['Active', 'Inactive'] },
    ],
    applyFilter: (
      items: RecurringPayable[],
      filters: Record<string, string[] | string>,
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>
    ) => {
      const ranges = dateRanges ?? {};
      return items.filter((item) => {
        const activeArr = Array.isArray(filters.isActive) ? filters.isActive : filters.isActive ? [filters.isActive] : [];
        if (activeArr.length > 0 && activeArr.some((v) => (v || '').trim())) {
          const status = item.isActive ? 'Active' : 'Inactive';
          if (!activeArr.includes(status)) return false;
        }

        const payeeFilter = typeof filters.payeeName === 'string' ? filters.payeeName : Array.isArray(filters.payeeName) ? (filters.payeeName as string[])[0] : '';
        if (payeeFilter && String(payeeFilter).trim()) {
          if (!(item.payeeName || '').toLowerCase().includes(String(payeeFilter).trim().toLowerCase())) return false;
        }

        const catArr = Array.isArray(filters.categoryId) ? filters.categoryId : filters.categoryId ? [filters.categoryId] : [];
        if (catArr.length > 0 && catArr.some((v) => (v || '').trim())) {
          const displayName = item.categoryDescr?.name ?? (item.categoryId != null ? `#${item.categoryId}` : '');
          const match = catArr.some((f) => {
            const v = String(f).trim();
            return displayName === v || (item.categoryId != null && v === `#${item.categoryId}`);
          });
          if (!match) return false;
        }

        const recurArr = Array.isArray(filters.recurrenceIntervalId) ? filters.recurrenceIntervalId : filters.recurrenceIntervalId ? [filters.recurrenceIntervalId] : [];
        if (recurArr.length > 0 && recurArr.some((v) => (v || '').trim())) {
          const displayName = item.recurrenceInterval?.tri_descr ?? (item.recurrenceIntervalId != null ? `#${item.recurrenceIntervalId}` : '');
          const match = recurArr.some((f) => {
            const v = String(f).trim();
            return displayName === v || (item.recurrenceIntervalId != null && v === `#${item.recurrenceIntervalId}`);
          });
          if (!match) return false;
        }

        if (filters.startDate && (Array.isArray(filters.startDate) ? filters.startDate : [filters.startDate]).length > 0) {
          const range = ranges.startDate;
          if (range?.start != null || range?.end != null) {
            const itemDate = item.startDate ? new Date(item.startDate) : null;
            if (!itemDate) return false;
            if (range.start != null && itemDate < range.start) return false;
            if (range.end != null && itemDate > range.end) return false;
          }
        }

        if (Array.isArray(filters.amount) && filters.amount.length > 0 && filters.amount.some((v) => (v || '').trim())) {
          const v = item.amount?.toString() || '';
          if (!filters.amount.some((f) => v === String(f).trim() || String(f).trim() === v)) return false;
        }

        return true;
      });
    },
  },
  toolbar: {
    actions: [
      { key: 'add', icon: 'add', title: 'Add Recurring Bill', showTitle: false, variant: 'primary' as const, href: '/recurring-payables/new' },
    ],
  },
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: RecurringPayable) => { window.location.href = `/recurring-payables/${item.id}`; },
    keyExtractor: (item: RecurringPayable) => item.id,
  },
  emptyStates: { noData: 'Add a recurring bill to project expenses.', noSearchResults: 'No matches.', noFilterResults: 'No matches.' },
  delete: {
    title: 'Delete Recurring Payable',
    message: 'Are you sure?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete.',
    handler: async (id: number) => {
      const { deleteRecurringPayable } = await import('../../services/mutations');
      await deleteRecurringPayable(id);
    },
  },
  utils: {
    formatDate: (dateString?: string) => (dateString ? new Date(dateString).toLocaleDateString() : '-'),
    formatCurrency: (amount?: number) =>
      amount == null ? '-' : new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount),
  },
  detail: {
    subtitle: 'Recurring Payable',
    notFoundTitle: 'Not found',
    notFoundMessage: 'This recurring payable does not exist.',
    notFoundBackText: 'Back to Recurring Bills',
    defaultTitle: 'Recurring Bill',
    getTitle: (item: RecurringPayable) => item.payeeName || `#${item.id}`,
  },
  form: {
    new: { title: 'New Recurring Bill', subtitle: 'Add a recurring payable', submitText: 'Create' },
    edit: { title: 'Edit Recurring Bill', subtitle: 'Update', submitText: 'Update' },
    cancelText: 'Cancel',
    loadingText: 'Loading recurring bill...',
    errorMessage: 'Failed to save.',
    create: async (data: Partial<RecurringPayable> & { recurrenceIntervalId?: string | number; categoryId?: string | number; subCategoryId?: string | number; payeeId?: string | number }) => {
      const { createRecurringPayable } = await import('../../services/mutations');
      const payload = {
        ...data,
        payeeId: data.payeeId != null && String(data.payeeId) !== '' ? Number(data.payeeId) : undefined,
        recurrenceIntervalId: data.recurrenceIntervalId != null && String(data.recurrenceIntervalId) !== '' ? Number(data.recurrenceIntervalId) : undefined,
        categoryId: data.categoryId != null && String(data.categoryId) !== '' ? Number(data.categoryId) : undefined,
        subCategoryId: data.subCategoryId != null && String(data.subCategoryId) !== '' ? Number(data.subCategoryId) : undefined,
      };
      return await createRecurringPayable(payload);
    },
    update: async (id: number, data: Partial<RecurringPayable> & { recurrenceIntervalId?: string | number; categoryId?: string | number; subCategoryId?: string | number; payeeId?: string | number }) => {
      const { updateRecurringPayable } = await import('../../services/mutations');
      const payload = {
        ...data,
        payeeId: data.payeeId != null && String(data.payeeId) !== '' ? Number(data.payeeId) : undefined,
        recurrenceIntervalId: data.recurrenceIntervalId != null && String(data.recurrenceIntervalId) !== '' ? Number(data.recurrenceIntervalId) : undefined,
        categoryId: data.categoryId != null && String(data.categoryId) !== '' ? Number(data.categoryId) : undefined,
        subCategoryId: data.subCategoryId != null && String(data.subCategoryId) !== '' ? Number(data.subCategoryId) : undefined,
      };
      return await updateRecurringPayable(id, payload);
    },
  },
  fields: [
    {
      name: 'payeeId',
      label: 'Payee',
      type: 'select',
      placeholder: 'Select payee',
      options: [{ value: '', label: 'Select...' }],
      apiEndpoint: '/payees',
      labelField: 'name',
      valueField: 'id' as const,
      validation: { required: true },
      onChangeSideEffect: async (value, _setFieldValue, setMultipleFieldValues) => {
        if (value == null || value === '') return;
        const api = (await import('../../services/api')).default;
        const res = await api.get(`/payees/${value}`);
        const payee = res.data as { defaultCategoryId?: number; defaultSubcategoryId?: number };
        if (payee?.defaultCategoryId != null) {
          setMultipleFieldValues({ categoryId: String(payee.defaultCategoryId) });
          if (payee?.defaultSubcategoryId != null) {
            setTimeout(() => {
              setMultipleFieldValues({ subCategoryId: String(payee.defaultSubcategoryId) });
            }, 800);
          }
        } else if (payee?.defaultSubcategoryId != null) {
          setMultipleFieldValues({ subCategoryId: String(payee.defaultSubcategoryId) });
        }
      },
    },
    { name: 'amount', label: 'Amount', type: 'number', placeholder: 'Amount', validation: { min: 0 } },
    {
      name: 'recurrenceIntervalId',
      label: 'Recurrence',
      type: 'select',
      placeholder: 'Monthly, Quarterly, etc.',
      options: [{ value: '', label: 'Select...' }],
      apiEndpoint: '/p-crm-todo-recurrence-interval',
      labelField: 'tri_descr',
      valueField: 'id' as const,
    },
    { name: 'dayOfMonth', label: 'Day of month (1-31)', type: 'number', placeholder: 'Optional' },
    { name: 'startDate', label: 'Start date', type: 'date', placeholder: 'When to start the recurring' },
    {
      name: 'categoryId',
      label: 'Category',
      type: 'select',
      placeholder: 'Optional',
      options: [{ value: '', label: 'None' }],
      apiEndpoint: '/crm-expense-category',
      labelField: 'name',
      valueField: 'id' as const,
    },
    {
      name: 'subCategoryId',
      label: 'Subcategory',
      type: 'select',
      placeholder: 'Select category first',
      options: [{ value: '', label: 'None' }],
      optionsEndpointFromFormData: (formData) => {
        const id = formData.categoryId;
        if (id == null || id === '') return null;
        return `/recurring-payables/subcategories/${Number(id)}`;
      },
      optionsDependOnFields: ['categoryId'],
      labelField: 'name',
      valueField: 'id' as const,
    },
    { name: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Notes', rows: 3 },
    { name: 'isActive', label: 'Active', type: 'checkbox' },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default' as const,
    sections: [
      {
        id: 'basic',
        title: 'Recurring Bill',
        icon: '🔄',
        fields: ['payeeId', 'amount', 'recurrenceIntervalId', 'dayOfMonth', 'startDate', 'categoryId', 'subCategoryId', 'notes', 'isActive'],
        collapsible: false,
      },
    ],
  },
};

export const getColumns = (formatCurrency: (a?: number) => string, colors?: ThemeColors): Column<RecurringPayable>[] => {
  const text = colors?.text || 'text-gray-900';
  const secondary = colors?.textSecondary || 'text-gray-700';
  return [
    { key: 'payeeName', header: 'Payee', sortable: true, render: (_, row) => <span className={`text-sm ${text}`}>{row.payeeName}</span> },
    { key: 'amount', header: 'Amount', sortable: true, render: (_, row) => <span className={`text-sm ${text} font-semibold`}>{formatCurrency(row.amount)}</span> },
    { key: 'recurrenceInterval', header: 'Recurrence', sortable: true, render: (_, row) => row.recurrenceInterval?.tri_descr || '-' },
    { key: 'dayOfMonth', header: 'Day', sortable: true, render: (_, row) => row.dayOfMonth ?? '-' },
    { key: 'startDate', header: 'Start date', sortable: true, render: (_, row) => (row.startDate ? moduleConfig.utils.formatDate(row.startDate) : '-') },
    {
      key: 'category',
      header: 'Category',
      sortable: true,
      width: '350px',
      render: (_, row) => (
        <div className="min-w-0">
          <div className={`text-sm ${text}`}>{row.categoryDescr?.name || '-'}</div>
          {row.subCategoryDescr?.name && (
            <div className={`text-xs ${secondary} truncate flex items-center gap-1`}>
              <svg className="w-3.5 h-3.5 shrink-0 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              {row.subCategoryDescr.name}
            </div>
          )}
        </div>
      ),
    },
    { key: 'isActive', header: 'Status', sortable: true, render: (_, row) => (
      <Badge variant={statusVariantFromBoolean(!!row.isActive)}>
        {row.isActive ? 'Active' : 'Inactive'}
      </Badge>
    ) },
  ];
};

export const getDetailSections = (item: RecurringPayable, formatCurrency: (a?: number) => string) => [
  {
    title: 'Recurring Bill',
    icon: '🔄',
    fields: [
      { label: 'Payee', value: item.payeeName },
      { label: 'Amount', value: formatCurrency(item.amount) },
      { label: 'Recurrence', value: item.recurrenceInterval?.tri_descr || '-' },
      { label: 'Day of month', value: item.dayOfMonth ?? '-' },
      { label: 'Start date', value: item.startDate ? moduleConfig.utils.formatDate(item.startDate) : '-' },
      { label: 'Category', value: item.categoryDescr?.name || '-' },
      { label: 'Subcategory', value: item.subCategoryDescr?.name || '-' },
      { label: 'Active', value: item.isActive ? 'Yes' : 'No' },
      { label: 'Notes', value: item.notes || '-' },
    ],
  },
  {
    title: 'Audit',
    icon: '📋',
    fields: [
      { label: 'Created By', value: item.createdBy?.userFirstName ?? '-' },
      { label: 'Updated By', value: item.updatedBy?.userFirstName ?? '-' },
    ],
  },
];

export const getStatusBadges = () => null;
export type RecurringPayableFormData = Partial<Omit<RecurringPayable, 'recurrenceIntervalId' | 'categoryId' | 'subCategoryId' | 'payeeId'>> & { recurrenceIntervalId?: string; categoryId?: string; subCategoryId?: string; payeeId?: string };
export const getInitialFormData = (): RecurringPayableFormData => ({
  payeeId: '',
  amount: undefined,
  recurrenceIntervalId: '',
  dayOfMonth: undefined,
  startDate: undefined,
  categoryId: '',
  subCategoryId: '',
  notes: '',
  isActive: true,
});
export const mapItemToFormData = (item: RecurringPayable): RecurringPayableFormData => ({
  payeeId: item.payeeId != null ? String(item.payeeId) : '',
  amount: item.amount,
  recurrenceIntervalId: item.recurrenceIntervalId != null ? String(item.recurrenceIntervalId) : '',
  dayOfMonth: item.dayOfMonth,
  startDate: item.startDate ?? undefined,
  categoryId: item.categoryId != null ? String(item.categoryId) : '',
  subCategoryId: item.subCategoryId != null ? String(item.subCategoryId) : '',
  notes: item.notes ?? '',
  isActive: item.isActive,
});
export const getToolbarIcon = (iconName: string) =>
  iconName === 'add' ? (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
  ) : null;

export const getTableActions = (
  item: RecurringPayable,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};

